import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserCapabilities, type UserCapabilities } from '@field-tickets/shared';
import { useAuthStore } from '@/store/auth';

/**
 * Route-level capability guard (defense-in-depth).
 *
 * The backend is the real security boundary — every protected API already gates
 * on the matching capability — but routes were previously only hidden from the
 * nav, so any signed-in user could URL-hop to /sales, /admin, /team, etc. and
 * land on a page that then 403s its data calls. This redirects those hops to
 * /no-access instead.
 *
 * Safe to redirect on a definitive `false`: this only renders inside AuthGuard's
 * <Outlet/>, which blocks until the user is loaded and authenticated, so `user`
 * is always present here.
 */
export function RequireCapability({
  capability,
  children,
}: {
  capability: keyof UserCapabilities;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const allowed = user ? getUserCapabilities(user)[capability] : false;
  if (!allowed) {
    return <Navigate to="/no-access" replace />;
  }
  return <>{children}</>;
}
